import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import Tooltip from '@mui/material/Tooltip';
import { type CSSProperties, useState } from 'react';
import { HighlightedPhrase } from '../../HighlightedPhrase/HighlightedPhrase';
import { ActionType, type HeaderListUnionProps, type ItemUnionTypeProps } from '../types';
import { ITEM_INDENTATION } from '../virtualList/constants';
import { StyledAddIcon, StyledBlockIcon, StyledCheckIcon, StyledRemoveIcon } from './Icons.styled';
import { StyledListItemText } from './ListItemText.styled';

export type ItemProps = {
  dataTest?: string;
  depth?: number;
  hideActionIcon?: boolean;
  id: string;
  isDisabled: boolean;
  isFrozen?: boolean;
  isOnList: boolean;
  name: string;
  searchInput: string;
  style: CSSProperties;
} & ItemUnionTypeProps &
  HeaderListUnionProps;

export const Item = (props: ItemProps) => {
  const {
    dataTest = 'item',
    depth = 0,
    hideActionIcon = false,
    id,
    isDisabled = false,
    isFrozen = false,
    isHeaderList,
    isOnList,
    isSelectedList,
    name,
    searchInput,
    style,
  } = props;
  const [showActions, setShowActions] = useState<boolean>(false);

  const hasSecondaryAction = showActions && !hideActionIcon;
  const isRemoveAction = isOnList || isFrozen;

  const onShowActions = () => setShowActions(true);
  const onHideActions = () => setShowActions(false);

  const handleRowClick = () => {
    if (isDisabled || (isFrozen && !isSelectedList)) return;

    if (isHeaderList) {
      props.handleRowClick(id, props.parentId);
      return;
    }

    props.handleRowClick(id);
  };

  return (
    <ListItemButton
      data-test={dataTest}
      dense
      disabled={isDisabled}
      onClick={handleRowClick}
      onMouseEnter={onShowActions}
      onMouseLeave={onHideActions}
      role="listitem"
      style={style}
      sx={{ pl: ITEM_INDENTATION + depth }}
    >
      {isSelectedList &&
        (props.actionType === ActionType.Block ? (
          <StyledBlockIcon data-test="BlockIcon" />
        ) : (
          <StyledCheckIcon data-test="CheckIcon" />
        ))}

      <Tooltip title={name} placement="top-start">
        <StyledListItemText
          primary={<HighlightedPhrase searchPhrase={searchInput}>{name}</HighlightedPhrase>}
          isSelected={isSelectedList ? !isOnList && !isFrozen : isOnList || isFrozen}
          disableTypography
        />
      </Tooltip>

      {hasSecondaryAction && (
        <IconButton
          aria-label={isRemoveAction ? 'Remove item' : 'Add item'}
          disableRipple
          disableFocusRipple
          edge="end"
        >
          {isRemoveAction ? <StyledRemoveIcon data-test="RemoveIcon" /> : <StyledAddIcon data-test="AddIcon" />}
        </IconButton>
      )}
    </ListItemButton>
  );
};
